import { useTranslation } from '@rhino-project/core/i18n';
import { Children, ReactNode, useCallback, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';
import { useModelEditContext } from '@rhino-project/core/hooks';
import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import { IconButton } from '../buttons';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useBackHistory } from '../../hooks';

// The saved record returned by the edit mutation; its shape is the model's
// record, which is dynamic here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SavedRecord = any;

interface ModelEditActionProps {
  children?: ReactNode;
  onSave?: (data: SavedRecord) => void;
  onCancel?: () => void;
  [key: string]: unknown;
}

export const ModelEditActionSave = ({
  children,
  onSave,
  ...props
}: ModelEditActionProps) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useModelEditContext();
  const { handleSubmit } = useFormContext();

  const onSuccess = useCallback(
    (data: SavedRecord) => {
      if (onSave) return onSave(data);
    },
    [onSave]
  );

  const handleClick = useCallback(
    () => handleSubmit((values) => mutate(values, { onSuccess }))(),
    [mutate, handleSubmit, onSuccess]
  );

  return (
    <IconButton
      color="primary"
      icon="bi:save"
      isPending={isPending}
      onPress={handleClick}
      {...props}
    >
      {children || t('rhino.frontend.messages.save')}
    </IconButton>
  );
};

export const ModelEditActionSaveShow = ({
  onSave,
  ...props
}: ModelEditActionProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSave = useCallback(
    (data: SavedRecord) => {
      if (onSave) onSave(data);

      // Dynamic relative navigation; the runtime `from` path doesn't match the
      // router's typed route union.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({ from: location.pathname, to: '..' } as any);
    },
    [location.pathname, navigate, onSave]
  );

  return <ModelEditActionSave onSave={handleSave} {...props} />;
};

export const ModelEditActionSaveBack = ({
  onSave,
  ...props
}: ModelEditActionProps) => {
  const backHistory = useBackHistory();
  const handleSave = useCallback(
    (data: SavedRecord) => {
      if (onSave) onSave(data);

      backHistory();
    },
    [backHistory, onSave]
  );

  return <ModelEditActionSave onSave={handleSave} {...props} />;
};

export const ModelEditActionCancel = ({
  children,
  onCancel,
  ...props
}: ModelEditActionProps) => {
  const { t } = useTranslation();
  const backHistory = useBackHistory();
  const handleClick = useCallback(
    () => (onCancel ? onCancel() : backHistory()),
    [onCancel, backHistory]
  );

  return (
    <IconButton outline icon="bi:x-square" onPress={handleClick} {...props}>
      {children || t('rhino.frontend.messages.cancel')}
    </IconButton>
  );
};

const defaultComponents = {
  ModelEditActionCancel,
  ModelEditActionSave: ModelEditActionSaveShow
};

export interface ModelEditActionsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  actions?: ReactNode;
  append?: boolean;
  prepend?: boolean;
  hasCancel?: boolean;
  [key: string]: unknown;
}

export const ModelEditActionsBase = ({
  overrides,
  actions,
  append = false,
  prepend = false,
  hasCancel = false,
  ...props
}: ModelEditActionsProps) => {
  // eslint-disable-next-line @eslint-react/static-components
  const { ModelEditActionCancel, ModelEditActionSave } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );

  const computedDefaultActions = useMemo(
    () =>
      // eslint-disable-next-line @eslint-react/no-missing-key, @eslint-react/static-components
      [hasCancel && <ModelEditActionCancel />, <ModelEditActionSave />].filter(
        Boolean
      ),
    // eslint-disable-next-line @eslint-react/exhaustive-deps, react-hooks/exhaustive-deps
    [hasCancel]
  );

  const computedActions = useMemo(() => {
    if (!actions) return computedDefaultActions;

    if (append)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...computedDefaultActions, ...Children.toArray(actions)];

    if (prepend)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...Children.toArray(actions), ...computedDefaultActions];

    return actions;
  }, [actions, append, prepend, computedDefaultActions]);

  return (
    <div className="flex flex-row flex-wrap justify-between" {...props}>
      {
        // eslint-disable-next-line @eslint-react/no-children-map
        Children.map(computedActions, (action) => action)
      }
    </div>
  );
};

export const ModelEditActions = (props: ModelEditActionsProps) =>
  useGlobalComponentForModel('ModelEditActions', ModelEditActionsBase, props);
